import React, { useState, useEffect } from 'react';
import { Briefcase, Plus, Edit2, Trash2, Users, MapPin, Clock, Eye } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';

interface Vaga {
  id: string;
  titulo: string;
  descricao: string;
  localidade: string;
  tipo_localidade: 'presencial' | 'remoto' | 'hibrido';
  tipo_vaga: string;
  requisitos?: string | null;
  beneficios?: string | null;
  status: 'ativa' | 'inativa';
  created_at: string;
}

const VagasRH: React.FC = () => {
  const [vagas, setVagas] = useState<Vaga[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showVagaDetail, setShowVagaDetail] = useState(false);
  const [editingVaga, setEditingVaga] = useState<Vaga | null>(null);
  const [selectedVaga, setSelectedVaga] = useState<Vaga | null>(null);
  const [formData, setFormData] = useState<{
    titulo: string;
    descricao: string;
    localidade: string;
    tipo_localidade: 'presencial' | 'remoto' | 'hibrido';
    tipo_vaga: string;
    requisitos: string;
    beneficios: string;
    status: 'ativa' | 'inativa';
  }>({
    titulo: '',
    descricao: '',
    localidade: '',
    tipo_localidade: 'presencial',
    tipo_vaga: '',
    requisitos: '',
    beneficios: '',
    status: 'ativa'
  });

  useEffect(() => {
    carregarVagas();
  }, []);

  const carregarVagas = async () => {
    try {
      const { data, error } = await supabase
        .from('vagas_rh')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVagas(data || []);
    } catch (error) {
      console.error('Erro ao carregar vagas:', error);
    } finally {
      setLoading(false);
    }
  };

  const salvarVaga = async () => {
    console.log('=== INICIANDO SALVAMENTO DA VAGA ===');
    console.log('Dados do formulário:', formData);
    console.log('Editing vaga:', editingVaga);
    
    try {
      // Validar campos obrigatórios
      console.log('Validando campos obrigatórios...');
      if (!formData.titulo.trim()) {
        console.log('Erro: título vazio');
        alert('Por favor, preencha o título da vaga.');
        return;
      }
      if (!formData.descricao.trim()) {
        console.log('Erro: descrição vazia');
        alert('Por favor, preencha a descrição da vaga.');
        return;
      }
      if (!formData.localidade.trim()) {
        console.log('Erro: localidade vazia');
        alert('Por favor, preencha a localidade.');
        return;
      }
      if (!formData.tipo_vaga.trim()) {
        console.log('Erro: tipo_vaga vazio');
        alert('Por favor, preencha o tipo de vaga.');
        return;
      }

      console.log('Todos os campos validados com sucesso');

      if (editingVaga) {
        console.log('Atualizando vaga existente...');
        const { data, error } = await supabase
          .from('vagas_rh')
          .update(formData)
          .eq('id', editingVaga.id)
          .select();

        console.log('Resultado da atualização:', { data, error });
        if (error) throw error;
        alert('Vaga atualizada com sucesso!');
      } else {
        console.log('Inserindo nova vaga...');
        const { data, error } = await supabase
          .from('vagas_rh')
          .insert(formData)
          .select();

        console.log('Resultado da inserção:', { data, error });
        if (error) throw error;
        alert('Vaga criada com sucesso!');
      }

      console.log('Salvamento concluído com sucesso');
      setShowForm(false);
      setEditingVaga(null);
      resetForm();
      carregarVagas();
    } catch (error: any) {
      console.error('=== ERRO DETALHADO ===');
      console.error('Erro completo:', error);
      console.error('Código do erro:', error?.code);
      console.error('Mensagem do erro:', error?.message);
      console.error('Detalhes do erro:', error?.details);
      console.error('Hint do erro:', error?.hint);
      alert('Erro ao salvar vaga');
    }
  };

  const deletarVaga = async (id: string) => {
    try {
      // Verificar se existem candidatos associados à vaga
      const { data: candidatos, error: candidatosError } = await supabase
        .from('candidatos_rh')
        .select('id')
        .eq('vaga_id', id);

      if (candidatosError) throw candidatosError;

      if (candidatos && candidatos.length > 0) {
        // Se existem candidatos, oferecer múltiplas opções
        const opcao = window.prompt(
          `Esta vaga possui ${candidatos.length} candidato(s) associado(s).\n\n` +
          'Escolha uma opção:\n' +
          '1 - Apenas desativar a vaga (recomendado)\n' +
          '2 - EXCLUIR TUDO (vaga + todos os candidatos)\n' +
          '3 - Cancelar\n\n' +
          'Digite o número da opção desejada (1, 2 ou 3):'
        );

        if (opcao === '1') {
          // Desativar vaga
          const { error } = await supabase
            .from('vagas_rh')
            .update({ status: 'inativa' })
            .eq('id', id);

          if (error) throw error;
          alert('Vaga desativada com sucesso!');
          carregarVagas();
        } else if (opcao === '2') {
          // Confirmar exclusão total
          const confirmacao = confirm(
            `⚠️ ATENÇÃO: AÇÃO IRREVERSÍVEL! ⚠️\n\n` +
            `Você está prestes a EXCLUIR PERMANENTEMENTE:\n` +
            `• A vaga\n` +
            `• ${candidatos.length} candidato(s) e todos os seus dados\n` +
            `• Histórico de entrevistas\n` +
            `• Documentos anexados\n\n` +
            `Esta ação NÃO PODE ser desfeita!\n\n` +
            `Tem ABSOLUTA CERTEZA que deseja continuar?`
          );

          if (confirmacao) {
            // Excluir primeiro os registros relacionados em cascata
            
            // 1. Excluir timeline dos candidatos
            await supabase
              .from('timeline_candidatos')
              .delete()
              .in('candidato_id', candidatos.map(c => c.id));

            // 2. Excluir conversas IA
            await supabase
              .from('conversas_ia_rh')
              .delete()
              .in('candidato_id', candidatos.map(c => c.id));

            // 3. Excluir entrevistas
            await supabase
              .from('entrevistas_rh')
              .delete()
              .in('candidato_id', candidatos.map(c => c.id));

            // 4. Excluir documentos
            await supabase
              .from('documentos_candidatos')
              .delete()
              .in('candidato_id', candidatos.map(c => c.id));

            // 5. Excluir candidatos
            await supabase
              .from('candidatos_rh')
              .delete()
              .eq('vaga_id', id);

            // 6. Finalmente, excluir a vaga
            const { error } = await supabase
              .from('vagas_rh')
              .delete()
              .eq('id', id);

            if (error) throw error;
            alert('Vaga e todos os dados relacionados foram excluídos permanentemente!');
            carregarVagas();
          }
        }
        // Opção 3 (cancelar) ou qualquer outra entrada - não faz nada
        return;
      }

      // Se não há candidatos, pode deletar normalmente
      if (!confirm('Tem certeza que deseja deletar esta vaga?')) return;

      const { error } = await supabase
        .from('vagas_rh')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('Vaga deletada com sucesso!');
      carregarVagas();
    } catch (error) {
      console.error('Erro ao processar vaga:', error);
      alert('Erro ao processar solicitação: ' + (error as any)?.message);
    }
  };

  const editarVaga = (vaga: Vaga) => {
    setEditingVaga(vaga);
    setFormData({
      titulo: vaga.titulo,
      descricao: vaga.descricao,
      localidade: vaga.localidade,
      tipo_localidade: vaga.tipo_localidade,
      tipo_vaga: vaga.tipo_vaga,
      requisitos: vaga.requisitos || '',
      beneficios: vaga.beneficios || '',
      status: vaga.status
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      descricao: '',
      localidade: '',
      tipo_localidade: 'presencial',
      tipo_vaga: '',
      requisitos: '',
      beneficios: '',
      status: 'ativa'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativa': return 'bg-green-100 text-green-800';
      case 'pausada': return 'bg-yellow-100 text-yellow-800';
      case 'fechada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoLocalidadeIcon = (tipo: string) => {
    switch (tipo) {
      case 'presencial': return '🏢';
      case 'remoto': return '🏠';
      case 'hibrido': return '🔄';
      default: return '📍';
    }
  };

  const getLocalidadeBadgeColor = (tipo: 'presencial' | 'remoto' | 'hibrido') => {
    switch (tipo) {
      case 'remoto':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'hibrido':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Briefcase className="w-8 h-8 text-orange-500 mr-3" />
            Gestão de Vagas
          </h1>
          <p className="text-gray-600">
            Gerencie as vagas disponíveis na empresa
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Nova Vaga</span>
        </button>
      </div>

      {/* Lista de Vagas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {vagas.map((vaga) => (
          <div key={vaga.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {vaga.titulo}
                </h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vaga.status)}`}>
                  {vaga.status}
                </span>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => editarVaga(vaga)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deletarVaga(vaga.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-4 line-clamp-3">
              {vaga.descricao}
            </p>

            <div className="space-y-2 text-sm text-gray-500">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-2" />
                <span>{getTipoLocalidadeIcon(vaga.tipo_localidade)} {vaga.localidade}</span>
              </div>
              <div className="flex items-center">
                <Briefcase className="w-4 h-4 mr-2" />
                <span>{vaga.tipo_vaga}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                <span>Criada em {new Date(vaga.created_at).toLocaleDateString('pt-BR')}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-500">
                  <Users className="w-4 h-4 mr-1" />
                  <span>0 candidatos</span>
                </div>
                <button 
                  onClick={() => {
                    setSelectedVaga(vaga);
                    setShowVagaDetail(true);
                  }}
                  className="text-orange-500 hover:text-orange-600 text-sm font-medium"
                >
                  Ver detalhes
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {vagas.length === 0 && (
        <div className="text-center py-12">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Nenhuma vaga cadastrada
          </h3>
          <p className="text-gray-600 mb-4">
            Comece criando sua primeira vaga
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg"
          >
            Criar Primeira Vaga
          </button>
        </div>
      )}

      {/* Modal do Formulário */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingVaga ? 'Editar Vaga' : 'Nova Vaga'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingVaga(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título da Vaga *
                  </label>
                  <input
                    type="text"
                    value={formData.titulo}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Ex: Vendedor Imobiliário"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Vaga *
                    </label>
                    <select
                      value={formData.tipo_vaga}
                      onChange={(e) => setFormData({ ...formData, tipo_vaga: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="">Selecione o tipo</option>
                      <option value="Corretor">Corretor</option>
                      <option value="Coordenador">Coordenador</option>
                      <option value="Gerente">Gerente</option>
                      <option value="Diretor">Diretor</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as 'ativa' | 'inativa' })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="ativa">Ativa</option>
                      <option value="inativa">Inativa</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Localização *
                    </label>
                    <select
                      value={formData.localidade}
                      onChange={(e) => setFormData({ ...formData, localidade: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="">Selecione a cidade</option>
                      <option value="Campinas">Campinas</option>
                      <option value="Paulínia">Paulínia</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Modalidade
                    </label>
                    <select
                      value={formData.tipo_localidade}
                      onChange={(e) => setFormData({ ...formData, tipo_localidade: e.target.value as 'presencial' | 'remoto' | 'hibrido' })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="presencial">Presencial</option>
                      <option value="remoto">Remoto</option>
                      <option value="hibrido">Híbrido</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição *
                  </label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Descreva as principais responsabilidades e informações da vaga..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Requisitos
                  </label>
                  <textarea
                    value={formData.requisitos}
                    onChange={(e) => setFormData({ ...formData, requisitos: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Liste os requisitos necessários para a vaga..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Benefícios
                  </label>
                  <textarea
                    value={formData.beneficios}
                    onChange={(e) => setFormData({ ...formData, beneficios: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Liste os benefícios oferecidos..."
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowPreview(true)}
                  className="flex items-center space-x-2 px-4 py-2 border border-orange-300 rounded-lg text-orange-600 hover:bg-orange-50"
                  disabled={!formData.titulo || !formData.descricao}
                >
                  <Eye className="w-4 h-4" />
                  <span>Pré-visualizar</span>
                </button>
                <button
                  onClick={salvarVaga}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-lg font-medium"
                >
                  {editingVaga ? 'Atualizar' : 'Criar'} Vaga
                </button>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingVaga(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pré-visualização */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Pré-visualização da Vaga
                </h2>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {/* Conteúdo da pré-visualização - similar ao VagaDetalhePage */}
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getLocalidadeBadgeColor(formData.tipo_localidade)}`}>
                        {getTipoLocalidadeIcon(formData.tipo_localidade)} {formData.tipo_localidade.charAt(0).toUpperCase() + formData.tipo_localidade.slice(1)}
                      </span>
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                        {formData.tipo_vaga}
                      </span>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {formData.titulo || 'Título da vaga'}
                    </h1>
                    <div className="flex items-center text-gray-600 mb-4">
                      <MapPin className="w-5 h-5 mr-2" />
                      <span>{formData.localidade || 'Localização'}</span>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-3">Descrição da Vaga</h2>
                      <div className="prose max-w-none">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {formData.descricao || 'Descrição da vaga aparecerá aqui...'}
                        </p>
                      </div>
                    </div>

                    {formData.requisitos && (
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Requisitos</h2>
                        <div className="prose max-w-none">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {formData.requisitos}
                          </p>
                        </div>
                      </div>
                    )}

                    {formData.beneficios && (
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Benefícios</h2>
                        <div className="prose max-w-none">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {formData.beneficios}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar */}
                <div className="lg:w-80">
                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Interessado na vaga?</h3>
                    <div className="space-y-3">
                      <button className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 px-4 rounded-lg font-medium transition-colors">
                        Candidatar-se
                      </button>
                      <button className="w-full border border-orange-300 text-orange-600 hover:bg-orange-50 py-3 px-4 rounded-lg font-medium transition-colors">
                        Conversar com IA
                      </button>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Sobre a Fox Vendas</h3>
                    <div className="space-y-3 text-sm text-gray-600">
                      <p>
                        A Fox Vendas é uma empresa líder no mercado imobiliário, 
                        com foco em excelência no atendimento e resultados excepcionais.
                      </p>
                      <div className="pt-4 border-t border-gray-200">
                        <p className="font-medium text-gray-900 mb-2">Entre em contato:</p>
                        <p>📧 aleska@foxvendas.com.br</p>
                        <p>📱 (19) 98606-7515</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg"
                >
                  Fechar Pré-visualização
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes da Vaga */}
      {showVagaDetail && selectedVaga && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Detalhes da Vaga
                </h2>
                <button
                  onClick={() => setShowVagaDetail(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {/* Conteúdo dos detalhes - similar ao modal de pré-visualização */}
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getLocalidadeBadgeColor(selectedVaga.tipo_localidade)}`}>
                        {getTipoLocalidadeIcon(selectedVaga.tipo_localidade)} {selectedVaga.tipo_localidade.charAt(0).toUpperCase() + selectedVaga.tipo_localidade.slice(1)}
                      </span>
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                        {selectedVaga.tipo_vaga}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedVaga.status)}`}>
                        {selectedVaga.status}
                      </span>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {selectedVaga.titulo}
                    </h1>
                    <div className="flex items-center text-gray-600 mb-4">
                      <MapPin className="w-5 h-5 mr-2" />
                      <span>{selectedVaga.localidade}</span>
                    </div>
                    <div className="flex items-center text-gray-500 text-sm mb-4">
                      <Clock className="w-4 h-4 mr-2" />
                      <span>Criada em {new Date(selectedVaga.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-3">Descrição da Vaga</h2>
                      <div className="prose max-w-none">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {selectedVaga.descricao}
                        </p>
                      </div>
                    </div>

                    {selectedVaga.requisitos && (
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Requisitos</h2>
                        <div className="prose max-w-none">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {selectedVaga.requisitos}
                          </p>
                        </div>
                      </div>
                    )}

                    {selectedVaga.beneficios && (
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Benefícios</h2>
                        <div className="prose max-w-none">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {selectedVaga.beneficios}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar */}
                <div className="lg:w-80">
                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Interessado na vaga?</h3>
                    <div className="space-y-3">
                      <button className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 px-4 rounded-lg font-medium transition-colors">
                        Candidatar-se
                      </button>
                      <button className="w-full border border-orange-300 text-orange-600 hover:bg-orange-50 py-3 px-4 rounded-lg font-medium transition-colors">
                        Conversar com IA
                      </button>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Sobre a Fox Vendas</h3>
                    <div className="space-y-3 text-sm text-gray-600">
                      <p>
                        A Fox Vendas é uma empresa líder no mercado imobiliário, 
                        com foco em excelência no atendimento e resultados excepcionais.
                      </p>
                      <div className="pt-4 border-t border-gray-200">
                        <p className="font-medium text-gray-900 mb-2">Entre em contato:</p>
                        <p>📧 aleska@foxvendas.com.br</p>
                        <p>📱 (19) 98606-7515</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowVagaDetail(false)}
                  className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VagasRH;